import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DIGIRASA_SESSION_EXPIRED_EVENT,
  extractApiErrorMessage,
  getFirstValue,
  readStoredAuth,
  writeStoredAuth,
} from '../../pages/Account/profileFormUtils';
import {
  apiRefreshAccessToken,
  performWebLogoutWithApi,
} from '../../pages/Account/authRecoveryApi';
import {
  markSessionExpiryPromptClosed,
  notifySessionExtended,
  scheduleSessionExpiryWatch,
} from '../../utils/sessionExpiry';
import './SessionExpiredModal.css';

function displayName(user) {
  if (!user || typeof user !== 'object') return '';
  const name = String(user.name || '').trim();
  if (name && name !== 'My Account') return name;
  return String(user.email || user.mobile || user.username || '').trim();
}

const SessionExpiredModal = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [canExtend, setCanExtend] = useState(true);
  const openRef = useRef(false);

  const openPrompt = useCallback(() => {
    const auth = readStoredAuth();
    if (!auth.token) return;
    if (openRef.current) return;
    openRef.current = true;
    setUser(auth.user || null);
    setCanExtend(Boolean(auth.refreshToken));
    setError('');
    setBusy('');
    setOpen(true);
  }, []);

  useEffect(() => {
    const onExpired = () => openPrompt();
    window.addEventListener(DIGIRASA_SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(DIGIRASA_SESSION_EXPIRED_EVENT, onExpired);
  }, [openPrompt]);

  const closePrompt = useCallback(() => {
    openRef.current = false;
    setOpen(false);
    setBusy('');
    setError('');
    markSessionExpiryPromptClosed();
  }, []);

  const handleLogout = async () => {
    setBusy('logout');
    setError('');
    try {
      await performWebLogoutWithApi();
    } finally {
      closePrompt();
      navigate('/', { replace: true });
    }
  };

  const handleExtend = async () => {
    const { refreshToken } = readStoredAuth();
    if (!refreshToken) {
      setCanExtend(false);
      setError('Your session cannot be extended. Please log out and sign in again.');
      return;
    }

    setBusy('extend');
    setError('');
    try {
      const { res, data } = await apiRefreshAccessToken(refreshToken);
      if (!res.ok) {
        const msg =
          extractApiErrorMessage(data) ||
          (res.status === 404 || res.status === 401
            ? 'Session could not be extended. Please log out and sign in again.'
            : 'Could not extend session. Try again or log out.');
        setError(msg);
        setCanExtend(res.status !== 404 && res.status !== 401);
        setBusy('');
        return;
      }

      const accessToken =
        getFirstValue(data, ['access_token', 'accessToken', 'token', 'data.access_token']) || '';
      if (!accessToken) {
        setError('Could not extend session. Please log out and sign in again.');
        setCanExtend(false);
        setBusy('');
        return;
      }

      writeStoredAuth({ token: accessToken });
      notifySessionExtended();
      closePrompt();
      scheduleSessionExpiryWatch();
    } catch {
      setError('Network error. Try again or log out.');
      setBusy('');
    }
  };

  if (!open) return null;

  const name = displayName(user);
  const email = user?.email ? String(user.email).trim() : '';
  const mobile = user?.mobile ? String(user.mobile).trim() : '';

  return (
    <div className="session-expired-overlay" role="presentation">
      <div
        className="session-expired-modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="session-expired-title"
        aria-describedby="session-expired-desc"
      >
        <h2 id="session-expired-title" className="session-expired-modal__title">
          Session expired
        </h2>
        <p id="session-expired-desc" className="session-expired-modal__desc">
          Your sign-in session has expired. You can extend it to stay signed in, or log out.
        </p>

        <div className="session-expired-modal__user">
          <span className="session-expired-modal__user-label">Signed in as</span>
          <strong className="session-expired-modal__user-name">{name || 'Your account'}</strong>
          {email ? <span className="session-expired-modal__user-meta">{email}</span> : null}
          {mobile && mobile !== email ? (
            <span className="session-expired-modal__user-meta">{mobile}</span>
          ) : null}
        </div>

        {error ? <p className="session-expired-modal__error">{error}</p> : null}

        <div className="session-expired-modal__actions">
          <button
            type="button"
            className="session-expired-modal__btn session-expired-modal__btn--secondary"
            onClick={handleLogout}
            disabled={Boolean(busy)}
          >
            {busy === 'logout' ? 'Logging out…' : 'Log out'}
          </button>
          {canExtend ? (
            <button
              type="button"
              className="session-expired-modal__btn session-expired-modal__btn--primary"
              onClick={handleExtend}
              disabled={Boolean(busy)}
            >
              {busy === 'extend' ? 'Extending…' : 'Extend session'}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default SessionExpiredModal;
